import { ref, watch, type Ref } from 'vue'

const VERSION_KEY_PREFIX = 'dt-storage-version'

/**
 * Version marker key for a single storage key. Versions are tracked per key: a single
 * shared marker would make two keys on different versions invalidate each other on
 * every page load, permanently wiping both caches.
 */
const versionKeyFor = (key: string) => `${VERSION_KEY_PREFIX}:${key}`

/**
 * Creates a reactive ref that's synced with localStorage.
 * On init, reads the stored value (falling back to defaultValue).
 * On change, writes back to localStorage (JSON-serialized).
 *
 * @param key - localStorage key
 * @param defaultValue - fallback value when nothing is stored
 * @param version - optional version string; if the stored version doesn't match,
 *                  the stored value is discarded (cache-busting on deploy)
 */
export function useLocalStorage<T>(key: string, defaultValue: T, version?: string): Ref<T> {
  if (version) {
    const versionKey = versionKeyFor(key)
    const storedVersion = localStorage.getItem(versionKey)
    if (storedVersion !== version) {
      localStorage.removeItem(key)
      localStorage.setItem(versionKey, version)
    }
  }

  let initial = defaultValue
  try {
    const stored = localStorage.getItem(key)
    if (stored !== null) {
      initial = JSON.parse(stored) as T
    }
  } catch {
    /* corrupt data — use default */
  }

  const data = ref<T>(initial) as Ref<T>

  watch(
    data,
    (val) => {
      try {
        if (val === undefined || val === null) {
          localStorage.removeItem(key)
        } else {
          localStorage.setItem(key, JSON.stringify(val))
        }
      } catch {
        /* quota exceeded or other storage error — silently ignore */
      }
    },
    { deep: true }
  )

  return data
}
